/**
 * Shared API client: timeout, auth, and consistent error handling.
 * Use for all backend requests so behaviour is professional and predictable.
 */

const DEFAULT_TIMEOUT_MS = 15000;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Fetch with timeout. Rejects with a descriptive error on timeout or network failure.
 * @param {string} url - Full URL or path (if path, BASE_URL is prepended)
 * @param {RequestInit} options - fetch options
 * @param {number} timeoutMs - timeout in ms
 * @returns {Promise<Response>}
 */
export function fetchWithTimeout(url, options = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL.replace(/\/$/, '')}${url.startsWith('/') ? '' : '/'}${url}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  // Do not chain .finally() on the native fetch promise — in some browsers it can interfere
  // with Response body / clone semantics when combined with DevTools or other tooling.
  return (async () => {
    try {
      return await fetch(fullUrl, {
        ...options,
        signal: controller.signal,
        headers: {
          ...(options.body && typeof options.body === 'string' && !options.headers?.['Content-Type'] ? { 'Content-Type': 'application/json' } : {}),
          ...options.headers,
        },
      });
    } finally {
      clearTimeout(timeoutId);
    }
  })();
}

/**
 * Read fetch Response body once. Prefer clone + arrayBuffer to avoid "body already used" / clone errors
 * that some environments hit when using .text() after the stream was touched.
 * @param {Response} response
 * @returns {Promise<string>}
 */
export async function readResponseBodyText(response) {
  try {
    const buf = await response.clone().arrayBuffer();
    return new TextDecoder('utf-8').decode(buf);
  } catch {
    try {
      const buf = await response.arrayBuffer();
      return new TextDecoder('utf-8').decode(buf);
    } catch {
      try {
        return await response.text();
      } catch {
        return '';
      }
    }
  }
}

/** When body could not be read, map status to a sensible message (e.g. auth). */
export function fallbackErrorForStatus(status, statusText = '') {
  if (status === 401) return 'Invalid credentials';
  if (status === 403) return 'Access denied';
  if (status === 404) return 'Not found';
  if (status >= 500) return statusText || `Server error (${status})`;
  return statusText || `Request failed (${status})`;
}

/**
 * Get auth header for admin, driver, or client.
 * @param {string} type - 'admin' | 'driver' | 'client'
 * @returns {{ Authorization: string } | {}}
 */
export function getAuthHeader(type = 'admin') {
  const key = type === 'admin' ? 'admin_token' : type === 'driver' ? 'driver_token' : 'session_token';
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

/**
 * Parse FastAPI-style error JSON from a response body string (single read — avoids double-consuming fetch body).
 * @param {string} text
 * @param {number} status
 * @param {string} [statusText]
 * @returns {string}
 */
export function parseErrorFromBodyText(text, status, statusText = '') {
  try {
    if (!text || !text.trim()) return statusText || `Error ${status}`;
    const body = JSON.parse(text);
    const d = body?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d) && d.length) {
      const first = d[0];
      const msg = first?.msg ?? first?.loc?.join?.('. ');
      return msg ? String(msg) : JSON.stringify(d[0]);
    }
    if (d && typeof d === 'object') return JSON.stringify(d);
    return statusText || `Error ${status}`;
  } catch {
    return text.trim().slice(0, 300) || statusText || `Error ${status}`;
  }
}

/**
 * Parse error body from API (consumes response body once via .text()).
 * @param {Response} response
 * @returns {Promise<string>} Human-readable error message
 */
export async function parseErrorDetail(response) {
  const text = await readResponseBodyText(response);
  if (!text || !text.trim()) {
    return fallbackErrorForStatus(response.status, response.statusText);
  }
  return parseErrorFromBodyText(text, response.status, response.statusText);
}

/**
 * Perform an API request with timeout and optional auth.
 * On non-2xx, returns { ok: false, error: string, status }.
 * On success, returns { ok: true, data } (parsed JSON).
 * On network/timeout, returns { ok: false, error: string }.
 *
 * @param {string} url - Path (e.g. '/api/auth/admin/login') or full URL
 * @param {RequestInit} options - fetch options (method, body, etc.)
 * @param {{ timeout?: number, auth?: 'admin'|'driver'|'client', parseJson?: boolean }} opts
 * @returns {Promise<{ ok: true, data: any } | { ok: false, error: string, status?: number }>}
 */
export async function apiRequest(url, options = {}, opts = {}) {
  const { timeout = DEFAULT_TIMEOUT_MS, auth, parseJson = true } = opts;
  const headers = {
    ...options.headers,
    ...(auth ? getAuthHeader(auth) : {}),
  };

  try {
    const response = await fetchWithTimeout(url, { ...options, headers }, timeout);
    const text = await readResponseBodyText(response);
    if (!response.ok) {
      const trimmed = text && text.trim();
      const error = trimmed
        ? parseErrorFromBodyText(text, response.status, response.statusText)
        : fallbackErrorForStatus(response.status, response.statusText);
      return { ok: false, error, status: response.status };
    }
    if (!parseJson) {
      return { ok: true, data: text };
    }
    try {
      const data = text ? JSON.parse(text) : null;
      return { ok: true, data };
    } catch {
      return {
        ok: false,
        error: 'Invalid JSON in server response',
        status: response.status,
      };
    }
  } catch (e) {
    const message =
      e.name === 'AbortError'
        ? 'Request timed out. Please try again.'
        : e.message || 'Network error. Please check your connection.';
    return { ok: false, error: message };
  }
}

export default {
  fetchWithTimeout,
  getAuthHeader,
  parseErrorDetail,
  parseErrorFromBodyText,
  readResponseBodyText,
  fallbackErrorForStatus,
  apiRequest,
};
